/**
 * Shared Brand Bible AI-suggest client for Sections A–I.
 *
 * Ground truth: backend/api/services/brand_bible_ai_suggest.py (Pydantic,
 * extra=forbid) and POST /brand-bible/ai-suggest with tenant_id as a QUERY
 * param (backend/api/routes/brand_bible.py).
 *
 * This seam never falls back to canned/demo text. HTTP failure rejects.
 * Drafts arrive unaccepted and unverified; callers must require explicit
 * human Accept before writing Brand Bible fields.
 */
import { apiClient } from '@/services/apiClient';

export type SectionCode = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H' | 'I';
export type OutputKind = 'chips' | 'text';
export type ClaimPolicy = 'creative' | 'grounded';
export type ProvenanceSource = 'response' | 'client' | 'router';

export interface ContextItem {
  key: string;
  value: string;
}

export interface TargetFieldMapping {
  output_key: string;
  target_field: string;
  instruction: string;
  kind: OutputKind;
  max_drafts: number;
  claim_policy: ClaimPolicy;
  existing: string[];
  dismissed: string[];
}

export interface AISuggestRequest {
  section: SectionCode;
  section_id: string;
  prompt?: string;
  targets: TargetFieldMapping[];
  tenant_context: ContextItem[];
  section_context: ContextItem[];
}

export interface FieldDraft {
  output_key: string;
  target_field: string;
  value: string;
  kind: OutputKind;
  source_context_keys: string[];
  /** Official client-site pages that support this specific field draft. */
  source_urls?: string[];
  status: 'draft';
  accepted: false;
  verified: false;
}

export interface FieldRefusal {
  output_key: string;
  target_field: string;
  reason: string;
  unsafe_categories: string[];
}

export interface ModelProvenance {
  provider: string;
  model: string;
  provider_source: ProvenanceSource;
  model_source: ProvenanceSource;
  requested_provider: string;
  requested_model: string;
}

/** Bounded metadata for the optional official-site retrieval stage. */
export interface RetrievalProvenance {
  attempted: boolean;
  status: 'not_needed' | 'used' | 'no_results' | 'unavailable';
  provider: 'exa' | null;
  official_domain: string | null;
  query: string | null;
  request_id: string | null;
  resolved_search_type: string | null;
  cache_hit: boolean;
  result_urls: string[];
}

export interface AISuggestResponse {
  tenant_id: string;
  section: SectionCode;
  section_id: string;
  drafts: FieldDraft[];
  refusals: FieldRefusal[];
  provenance: ModelProvenance;
  /** Optional first DeepSeek call that decides whether/what Exa should fetch. */
  planner_provenance?: ModelProvenance | null;
  /** Optional Exa retrieval metadata; `provenance` identifies the final call. */
  retrieval?: RetrievalProvenance | null;
  provider: string;
  model: string;
  source: string;
}

/** Injectable, tenant-bound fetch used by section panels and tests. */
export type FetchFieldSuggestions = (request: AISuggestRequest) => Promise<AISuggestResponse>;

/**
 * POST /brand-bible/ai-suggest. `tenant_id` is a required query param.
 * Rejects on missing tenant, HTTP failure, or a response missing drafts/refusals.
 * Never substitutes canned items.
 */
export async function requestModelFieldSuggestions(
  request: AISuggestRequest,
  tenantId: string | null,
): Promise<AISuggestResponse> {
  if (!tenantId || !tenantId.trim()) {
    throw new Error('tenant_id is required for Brand Bible AI suggestions');
  }
  const { data } = await apiClient.post<AISuggestResponse>('/brand-bible/ai-suggest', request, {
    params: { tenant_id: tenantId },
    // Two 60s DeepSeek calls + one 8s Exa call can exceed the global 30s client.
    timeout: 140000,
  });
  if (!data || !Array.isArray(data.drafts) || !Array.isArray(data.refusals)) {
    throw new Error('Brand Bible AI suggest response is missing drafts or refusals');
  }
  return data;
}

/** Runtime guard: drafts must arrive unaccepted, unverified, and non-blank. */
export function isValidUnverifiedDraft(draft: FieldDraft): boolean {
  return (
    draft.status === 'draft' &&
    draft.accepted === false &&
    draft.verified === false &&
    typeof draft.value === 'string' &&
    draft.value.trim() !== ''
  );
}
